/**
 * Thin fetch wrapper for the course appointment API.
 *
 * The base URL points at the `course-management/course-appointment-api` route
 * (without an action). The action segment + HTTP method select the controller
 * action (e.g. `create-instance` + POST => createInstancePostAction).
 */

async function request(baseUrl, action, method, payload) {
	const url = `${baseUrl.replace(/\/$/, "")}/${action}`;
	const response = await fetch(url, {
		method,
		headers: {
			"Content-Type": "application/json",
			"X-Requested-With": "XMLHttpRequest",
		},
		credentials: "include",
		body: method === "GET" ? undefined : JSON.stringify(payload ?? {}),
	});

	let data = {};
	try {
		data = await response.json();
	} catch {
		// no-op: keep empty object
	}

	if (!response.ok || data.error || data.invalid) {
		const error = new Error(data.error || `HTTP ${response.status}`);
		error.response = data;
		throw error;
	}

	return data;
}

export const createInstance = (baseUrl, payload) =>
	request(baseUrl, "create-instance", "POST", payload);

export const createSeries = (baseUrl, payload) =>
	request(baseUrl, "create-series", "POST", payload);

export const updateInstance = (baseUrl, payload) =>
	request(baseUrl, "update-instance", "PUT", payload);

export const updateSeries = (baseUrl, payload) =>
	request(baseUrl, "update-series", "PUT", payload);

export const deleteInstance = (baseUrl, id) =>
	request(baseUrl, "delete-instance", "DELETE", { id });

export const deleteSeries = (baseUrl, id) =>
	request(baseUrl, "delete-series", "DELETE", { id });

export const fetchSemesters = (baseUrl) =>
	request(baseUrl, "semesters", "GET");

export const fetchCourses = (baseUrl, semesterId) =>
	request(baseUrl, "courses", "POST", { semesterId });

export const fetchSourceAppointments = (baseUrl, courseId) =>
	request(baseUrl, "source-appointments", "POST", { courseId });

export const copyAppointments = (baseUrl, payload) =>
	request(baseUrl, "copy", "POST", payload);

export const fetchBookableAppointments = (baseUrl) =>
	request(baseUrl, "bookable-appointments", "GET");

export const bookAppointment = (baseUrl, payload) =>
	request(baseUrl, "book", "POST", payload);

export const unbookAppointment = (baseUrl, payload) =>
	request(baseUrl, "unbook", "POST", payload);

export const fetchConflicts = (baseUrl) =>
	request(baseUrl, "conflicts", "GET");

export const checkConflicts = (baseUrl, payload) =>
	request(baseUrl, "check-conflicts", "POST", payload);
